
import {
    buildPlannerColumnAccessOpts,
    normalizeBoardStatistics,
    normalizeHorizontalScrollTimeline,
    normalizePlannerColumns,
    normalizeShowDriverPayoutGross,
} from '../main_board_components/plannerBoardSettings';

const PERMISSIONS_KEY = 'permissions';

const readPermissions = () => {
    try {
        const raw = localStorage.getItem(PERMISSIONS_KEY);
        if (!raw || raw === 'undefined') return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
        return {};
    }
};

const covers = (have, required) => {
    if (!required || required === 'NONE') return true;
    const haveUpper = String(have ?? '').toUpperCase();
    if (haveUpper === 'MANAGE' || haveUpper === 'TRUE' || have === true) return true;
    if (required === 'VIEW') return haveUpper === 'VIEW';
    return false;
};

/** Permission check from localStorage only — safe during login / customization normalize. */
export const can = (key, level = 'VIEW') => {
    if (!key) return false;
    const stored = readPermissions()[key];
    if (stored == null) return false;
    if (level == null || String(level).toUpperCase() === 'FLAG') {
        return stored === true || String(stored).toUpperCase() === 'TRUE' || String(stored).toUpperCase() === 'MANAGE';
    }
    return covers(stored, String(level).toUpperCase());
};

export const flag = (key) => can(key, 'FLAG');

export const isCompanyAdmin = () =>
    flag('COMPANY_ADMIN') || localStorage.getItem('roleCode') === 'COMPANY_ADMIN';

const LEGACY_ACCESS = {
    11: true,
    12: { key: 'RATES' },
    13: { key: 'EDITING_LOCKED_LOADS' },
    21: { key: 'TRAILERS', level: 'MANAGE' },
    22: { key: 'DRIVERS', level: 'MANAGE' },
    23: { key: 'USERS', level: 'VIEW' },
    31: { key: 'MONTHLY_REPORTS', level: 'MANAGE' },
    32: { key: 'TRUCKS', level: 'MANAGE' },
    33: { key: 'TRAILER_MANAGEMENT' },
    41: { key: 'INVOICES', level: 'VIEW' },
    42: { key: 'INVOICES', level: 'MANAGE' },
    43: { key: 'COMPANY_ADMIN' },
};

export const setUserData = (e, orgId, activeCompanyId) => {
    if(e === null)return;

    localStorage.setItem('currentUserId', e.id);
    localStorage.setItem('currentUserDispatches', e.dispatches);
    localStorage.setItem('currentUserFirstName', e.firstName);
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(e.permissions || {}));
    localStorage.setItem('roleId', e.roleId != null ? String(e.roleId) : '');
    localStorage.setItem('roleName', e.roleName || '');
    localStorage.setItem('roleCode', e.roleCode || '');
    const companiesList = [];
    for(const company of e.accessibleCompanies || []){
        companiesList.push(typeof(company) === 'string' ? JSON.parse(company) : company)
    }
    localStorage.setItem('accessibleCompanies', JSON.stringify(companiesList));
    localStorage.setItem('orgId', orgId);

    const customizations = normalizeMainBoardCustomizations(e.mainBoardCustomizations);
    localStorage.setItem('mainBoardCustomizations', JSON.stringify(customizations));
    try {
        const reportPrefs = e.reportCustomizations && typeof e.reportCustomizations === 'object' && !Array.isArray(e.reportCustomizations)
            ? e.reportCustomizations
            : {};
        localStorage.setItem('reportCustomizations', JSON.stringify(reportPrefs));
    } catch (_) {
        localStorage.setItem('reportCustomizations', '{}');
    }
    storeCustomizations(e.customizations);
    const companyIdForPrefs = activeCompanyId != null
        ? activeCompanyId
        : (e.company?.id ?? e.company?.Id);
    syncMainBoardPrefsToLocalStorage(customizations, companyIdForPrefs);
}

const CUSTOMIZATIONS_KEY = 'customizations';

/** Personal customizations (employee.customizations), keyed by customization id. */
export const retrieveCustomizations = () => {
    try {
        const raw = localStorage.getItem(CUSTOMIZATIONS_KEY);
        if (!raw || raw === 'undefined') return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
        return {};
    }
};

export const retrieveCustomization = (key) => retrieveCustomizations()[key] ?? null;

export const storeCustomizations = (customizations) => {
    const value = customizations && typeof customizations === 'object' && !Array.isArray(customizations)
        ? customizations
        : {};
    localStorage.setItem(CUSTOMIZATIONS_KEY, JSON.stringify(value));
    window.dispatchEvent(new Event('customizations-changed'));
};

/** Ensure expected shape for mainBoardCustomizations (teams per company; other prefs global). */
export const normalizeMainBoardCustomizations = (raw) => {
    const source = raw && typeof raw === 'object' ? raw : {};
    let selectedTeamsList = source.selectedTeamsList;
    if (!selectedTeamsList || typeof selectedTeamsList !== 'object' || Array.isArray(selectedTeamsList)) {
        selectedTeamsList = {};
    }
    const normalizedTeams = {};
    for (const [companyId, teams] of Object.entries(selectedTeamsList)) {
        if (Array.isArray(teams)) {
            normalizedTeams[String(companyId)] = teams.map(String);
        } else if (typeof teams === 'string') {
            try {
                const parsed = JSON.parse(teams);
                normalizedTeams[String(companyId)] = Array.isArray(parsed) ? parsed.map(String) : [];
            } catch (_) {
                normalizedTeams[String(companyId)] = [];
            }
        } else {
            normalizedTeams[String(companyId)] = [];
        }
    }

    const ratesAccess = canSeeLoadRatesFromStorage();
    const cannotSeePayouts = !can('DRIVER_PAYOUTS', 'VIEW');

    return {
        ...source,
        plannerViewMode: source.plannerViewMode !== false && source.plannerViewMode !== 'false',
        selectedTeamsList: normalizedTeams,
        horizontalScrollTimeline: normalizeHorizontalScrollTimeline(source.horizontalScrollTimeline),
        plannerColumns: normalizePlannerColumns(
            source.plannerColumns,
            buildPlannerColumnAccessOpts({ userCanSeeRates: ratesAccess, userIsUpdater: cannotSeePayouts })
        ),
        boardStatistics: normalizeBoardStatistics(source.boardStatistics, {
            userCanSeeRates: ratesAccess,
        }),
        showDriverPayoutGross: normalizeShowDriverPayoutGross(source.showDriverPayoutGross),
    };
};

export const retrieveMainBoardCustomizations = () => {
    try {
        const raw = localStorage.getItem('mainBoardCustomizations');
        if (raw == null || raw === '' || raw === 'undefined') return normalizeMainBoardCustomizations({});
        return normalizeMainBoardCustomizations(JSON.parse(raw));
    } catch (_) {
        return normalizeMainBoardCustomizations({});
    }
};

/** Mirror planner / teams into mainBoardCustomizations (and legacy keys) for the active company. */
export const syncMainBoardPrefsToLocalStorage = (customizations, companyId) => {
    const prefs = normalizeMainBoardCustomizations(customizations);
    localStorage.setItem('mainBoardCustomizations', JSON.stringify(prefs));
    localStorage.setItem('plannerViewMode', JSON.stringify(prefs.plannerViewMode !== false));
    const key = companyId != null ? String(companyId) : null;
    const teams = key && prefs.selectedTeamsList[key] ? prefs.selectedTeamsList[key] : [];
    localStorage.setItem('selectedTeamsList', JSON.stringify(teams));
};

/**
 * Update local mainBoardCustomizations for planner mode and/or this company's selected teams
 * and/or planner UI settings (scroll, columns, statistics).
 * Keeps legacy plannerViewMode / selectedTeamsList keys in sync for any remaining readers.
 */
export const updateLocalMainBoardCustomizations = ({
    companyId,
    plannerViewMode,
    selectedTeamsForCompany,
    horizontalScrollTimeline,
    plannerColumns,
    boardStatistics,
    showDriverPayoutGross,
} = {}) => {
    const prefs = retrieveMainBoardCustomizations();
    if (plannerViewMode !== undefined) {
        prefs.plannerViewMode = plannerViewMode !== false && plannerViewMode !== 'false';
    }
    if (companyId != null && selectedTeamsForCompany !== undefined) {
        prefs.selectedTeamsList = {
            ...prefs.selectedTeamsList,
            [String(companyId)]: Array.isArray(selectedTeamsForCompany)
                ? selectedTeamsForCompany.map(String)
                : [],
        };
    }
    if (horizontalScrollTimeline !== undefined) {
        prefs.horizontalScrollTimeline = normalizeHorizontalScrollTimeline(horizontalScrollTimeline);
    }
    if (plannerColumns !== undefined) {
        prefs.plannerColumns = normalizePlannerColumns(
            plannerColumns,
            buildPlannerColumnAccessOpts({
                userCanSeeRates: canSeeLoadRatesFromStorage(),
                userIsUpdater: !can('DRIVER_PAYOUTS', 'VIEW'),
            })
        );
    }
    if (boardStatistics !== undefined) {
        prefs.boardStatistics = normalizeBoardStatistics(boardStatistics, {
            userCanSeeRates: canSeeLoadRatesFromStorage(),
        });
    }
    if (showDriverPayoutGross !== undefined) {
        prefs.showDriverPayoutGross = normalizeShowDriverPayoutGross(showDriverPayoutGross);
    }
    syncMainBoardPrefsToLocalStorage(prefs, companyId);
};

export const retrieveUserData = () => {
    return {
        id : localStorage.getItem("currentUserId"),
        firstName: localStorage.getItem("currentUserFirstName"),
        permissions: readPermissions(),
        roleId: localStorage.getItem("roleId"),
        roleName: localStorage.getItem("roleName"),
        roleCode: localStorage.getItem("roleCode"),
        accessibleCompanies:localStorage.getItem("accessibleCompanies"),
        isCompanyAdmin: isCompanyAdmin(),
        canSeeDriverPayouts: can('DRIVER_PAYOUTS', 'VIEW'),
        canManageDriverPayouts: can('DRIVER_PAYOUTS', 'MANAGE'),
        mainBoardCustomizations: retrieveMainBoardCustomizations(),
    }
}

const matchesPermissionSpec = (spec) => {
    if (!spec) return false;
    if (spec === true) return true;
    if (typeof spec === 'object' && spec.key) {
        if (spec.level) return can(spec.key, spec.level);
        return flag(spec.key) || can(spec.key, 'VIEW');
    }
    const mapped = LEGACY_ACCESS[spec] ?? LEGACY_ACCESS[Number(spec)];
    if (mapped === true) return true;
    if (mapped) return matchesPermissionSpec(mapped);
    return false;
};

export const userHasAccess = (accessMinimumLvl) => {
    if(!accessMinimumLvl)return false;
    if(accessMinimumLvl === true)return true;
    if(Array.isArray(accessMinimumLvl)){
        return accessMinimumLvl.some(matchesPermissionSpec);
    }
    return matchesPermissionSpec(accessMinimumLvl);
}

/** Department gates are gone; leftover confs that still pass a department are ignored. */
export const userBelongsToCorrectDepartment = () => true;

/** Rate visibility from localStorage only — safe to call from normalize/customization paths. */
const canSeeLoadRatesFromStorage = () => flag('RATES');

export const canSeeLoadRates = () => canSeeLoadRatesFromStorage();
